import React, { useRef, useState, useEffect, useCallback } from 'react';
import { openCVLoader } from '../../services/omr/OpenCVLoader';
import { OMREngine } from '../../services/omr/OMREngine';
import { OMR_TEMPLATES, getTemplateById } from '../../services/omr/OMRTemplateRegistry';
import { XCircleIcon, CameraIcon } from '../icons';

interface OMRScannerProps {
    onClose: () => void;
    onResults: (results: any) => void;
    templateId?: string;
}

type ScanStep = 'capture' | 'processing' | 'preview';

export const OMRScanner: React.FC<OMRScannerProps> = ({ onClose, onResults, templateId = 'yks-optik-129' }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [step, setStep] = useState<ScanStep>('capture');
    const [isLibReady, setIsLibReady] = useState(false);
    const [loadProgress, setLoadProgress] = useState('Hazırlanıyor...');
    const [error, setError] = useState<string | null>(null);
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [activeTemplateId, setActiveTemplateId] = useState(templateId);
    const [useCamera, setUseCamera] = useState(false);

    // Results state
    const [scanResults, setScanResults] = useState<Record<string, any[]>>({});
    const [debugInfo, setDebugInfo] = useState<any>(null);
    const [debugImage, setDebugImage] = useState<string | null>(null);
    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const [showDebug, setShowDebug] = useState(false);

    const template = getTemplateById(activeTemplateId) || OMR_TEMPLATES[0];

    // Initialize OpenCV
    useEffect(() => {
        setLoadProgress('Görüntü İşleme Modülü Yükleniyor...');
        openCVLoader.load()
            .then(() => {
                setIsLibReady(true);
                setLoadProgress('Hazır');
            })
            .catch(err => {
                setError('OpenCV yüklenemedi: ' + err.message);
                setLoadProgress('Hata!');
            });

        return () => {
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
        };
    }, []);

    // Start camera
    const startCamera = useCallback(async () => {
        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: 'environment',
                    width: { ideal: 1920 },
                    height: { ideal: 1080 }
                }
            });
            setStream(mediaStream);
            setUseCamera(true);
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
            }
        } catch (err) {
            setError('Kameraya erişilemedi. Dosya yükleme kullanabilirsiniz.');
        }
    }, []);

    const stopCamera = useCallback(() => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
        setUseCamera(false);
    }, [stream]);

    // Process image on canvas
    const processCanvas = useCallback(async (canvas: HTMLCanvasElement) => {
        if (!isLibReady) return;

        setStep('processing');
        setError(null);
        setLoadProgress('Form Analiz Ediliyor...');

        try {
            const engine = new OMREngine(openCVLoader.getCV());
            const { results, debug, debugImage: img } = await engine.processImage(canvas, template);

            setScanResults(results);
            setDebugInfo(debug);
            setDebugImage(img || null);
            setStep('preview');
        } catch (err: any) {
            setError(err.message || 'Optik form okunamadı.');
            setStep('capture');
        }
    }, [isLibReady, template]);

    // Handle camera capture
    const handleCameraCapture = useCallback(async () => {
        if (!videoRef.current || !canvasRef.current) return;

        const video = videoRef.current;
        const canvas = canvasRef.current;

        if (video.videoWidth === 0 || video.videoHeight === 0) {
            setError('Kamera görüntüsü hazır değil. Tekrar deneyin.');
            return;
        }

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Save preview
        setPreviewImage(canvas.toDataURL('image/jpeg', 0.8));

        // Stop camera
        stopCamera();

        await processCanvas(canvas);
    }, [processCanvas, stopCamera]);

    // Handle file upload
    const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setError(null);

        const img = new Image();
        img.onload = async () => {
            const canvas = canvasRef.current;
            if (!canvas) return;

            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            if (!ctx) return;
            ctx.drawImage(img, 0, 0);

            // Save preview
            setPreviewImage(canvas.toDataURL('image/jpeg', 0.8));

            await processCanvas(canvas);
        };

        img.onerror = () => {
            setError('Dosya okunamadı. Lütfen geçerli bir resim dosyası seçin.');
        };

        img.src = URL.createObjectURL(file);
    }, [processCanvas]);

    // Edit an answer in preview
    const handleEditAnswer = (sectionName: string, questionIdx: number, newAnswer: string) => {
        setScanResults(prev => {
            const updated = { ...prev };
            if (updated[sectionName] && updated[sectionName][questionIdx]) {
                updated[sectionName] = [...updated[sectionName]];
                updated[sectionName][questionIdx] = {
                    ...updated[sectionName][questionIdx],
                    answer: newAnswer
                };
            }
            return updated;
        });
    };

    // Confirm and send results
    const handleConfirm = () => {
        onResults(scanResults);
        onClose();
    };

    // Retry
    const handleRetry = () => {
        setScanResults({});
        setDebugInfo(null);
        setDebugImage(null);
        setPreviewImage(null);
        setStep('capture');
        setError(null);
    };

    // Get total stats
    const getTotalStats = () => {
        let total = 0, marked = 0;
        Object.values(scanResults).forEach((answers: any[]) => {
            answers.forEach(a => {
                total++;
                if (a.answer) marked++;
            });
        });
        return { total, marked, empty: total - marked };
    };

    const stats = step === 'preview' ? getTotalStats() : null;

    return (
        <div className="fixed inset-0 z-[100] bg-slate-900 flex flex-col">
            {/* Header */}
            <div className="shrink-0 px-4 pt-4 pb-3 flex justify-between items-center bg-gradient-to-b from-slate-800 to-transparent z-10">
                <div className="flex-1 min-w-0">
                    {step === 'capture' && (
                        <div className="relative inline-block">
                            <select
                                value={activeTemplateId}
                                onChange={(e) => setActiveTemplateId(e.target.value)}
                                className="bg-white/10 border border-white/20 text-white font-bold py-2 px-4 pr-8 rounded-xl appearance-none outline-none text-sm backdrop-blur-md"
                            >
                                {OMR_TEMPLATES.map(t => (
                                    <option key={t.id} value={t.id} className="bg-slate-900 text-white">
                                        {t.name}
                                    </option>
                                ))}
                            </select>
                            <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-white/60">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </div>
                    )}
                    {step === 'preview' && (
                        <h3 className="text-white font-black text-sm tracking-wider">
                            📋 SONUÇ ÖNİZLEME
                        </h3>
                    )}
                    {step === 'processing' && (
                        <h3 className="text-blue-300 font-bold text-sm animate-pulse">
                            ⏳ İşleniyor...
                        </h3>
                    )}
                </div>
                <button
                    onClick={() => { stopCamera(); onClose(); }}
                    className="text-white/60 hover:text-white transition-colors bg-white/5 p-2 rounded-full hover:bg-white/10 ml-2"
                >
                    <XCircleIcon className="w-8 h-8" />
                </button>
            </div>

            {/* Main Content */}
            <div className="flex-1 overflow-hidden relative">

                {/* ─── STEP: CAPTURE ─── */}
                {step === 'capture' && (
                    <div className="h-full flex flex-col items-center justify-center p-6 gap-6">
                        {/* Camera Preview */}
                        {useCamera && (
                            <div className="relative w-full max-w-lg aspect-[3/4] bg-black rounded-3xl overflow-hidden border-2 border-white/10 shadow-2xl">
                                <video
                                    ref={videoRef}
                                    autoPlay
                                    playsInline
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                                {/* Guide overlay */}
                                <div className="absolute inset-4 border-2 border-dashed border-blue-400/40 rounded-2xl pointer-events-none">
                                    <div className="absolute -top-0.5 -left-0.5 w-6 h-6 border-t-3 border-l-3 border-blue-400 rounded-tl-lg" />
                                    <div className="absolute -top-0.5 -right-0.5 w-6 h-6 border-t-3 border-r-3 border-blue-400 rounded-tr-lg" />
                                    <div className="absolute -bottom-0.5 -left-0.5 w-6 h-6 border-b-3 border-l-3 border-blue-400 rounded-bl-lg" />
                                    <div className="absolute -bottom-0.5 -right-0.5 w-6 h-6 border-b-3 border-r-3 border-blue-400 rounded-br-lg" />
                                </div>
                            </div>
                        )}

                        {/* Upload Area (when not using camera) */}
                        {!useCamera && (
                            <div className="w-full max-w-md">
                                {/* Main upload button */}
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={!isLibReady}
                                    className="w-full aspect-[4/3] bg-gradient-to-br from-slate-800 to-slate-900 border-2 border-dashed border-white/20 hover:border-blue-400/60 rounded-3xl flex flex-col items-center justify-center gap-4 transition-all group cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <div className="w-20 h-20 rounded-full bg-blue-500/10 group-hover:bg-blue-500/20 flex items-center justify-center transition-all">
                                        <svg className="w-10 h-10 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-white font-black text-lg">
                                            {isLibReady ? 'Optik Form Fotoğrafı Seçin' : 'Modül Yükleniyor...'}
                                        </p>
                                        <p className="text-white/40 text-xs mt-1 font-medium">
                                            Galeriden seçin veya fotoğraf çekin
                                        </p>
                                    </div>
                                </button>

                                {/* Loading indicator */}
                                {!isLibReady && (
                                    <div className="mt-4 flex items-center justify-center gap-3">
                                        <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                        <span className="text-blue-300 text-sm font-bold">{loadProgress}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Action buttons */}
                        <div className="flex gap-3 w-full max-w-md">
                            {!useCamera ? (
                                <>
                                    <button
                                        onClick={startCamera}
                                        disabled={!isLibReady}
                                        className="flex-1 py-4 bg-white/10 hover:bg-white/15 border border-white/10 text-white rounded-2xl font-bold text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-40"
                                    >
                                        <CameraIcon className="w-5 h-5" /> Kamera Aç
                                    </button>
                                    <button
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={!isLibReady}
                                        className="flex-1 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-500/25 disabled:opacity-40"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                        </svg>
                                        Dosya Seç
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        onClick={() => stopCamera()}
                                        className="py-4 px-6 bg-white/10 hover:bg-white/15 text-white rounded-2xl font-bold text-sm transition-all"
                                    >
                                        ← Geri
                                    </button>
                                    <button
                                        onClick={handleCameraCapture}
                                        disabled={!isLibReady}
                                        className="flex-1 py-4 bg-white hover:bg-gray-100 text-slate-900 rounded-2xl font-black text-sm transition-all flex items-center justify-center gap-2 shadow-lg shadow-white/20 disabled:opacity-40 active:scale-95"
                                    >
                                        <CameraIcon className="w-6 h-6" /> Fotoğraf Çek
                                    </button>
                                </>
                            )}
                        </div>

                        {/* Template info */}
                        <div className="text-center">
                            <p className="text-white/30 text-[10px] font-bold uppercase tracking-widest">
                                Şablon: {template.name} • {template.layout.sections.reduce((a, s) => a + s.questionCount, 0)} Soru
                            </p>
                        </div>
                    </div>
                )}

                {/* ─── STEP: PROCESSING ─── */}
                {step === 'processing' && (
                    <div className="h-full flex flex-col items-center justify-center p-8">
                        {previewImage && (
                            <div className="w-48 h-64 rounded-2xl overflow-hidden mb-6 border border-white/10 shadow-xl opacity-60">
                                <img src={previewImage} className="w-full h-full object-cover" alt="Taranan form" />
                            </div>
                        )}
                        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
                        <p className="text-white font-black text-lg">{loadProgress}</p>
                        <p className="text-white/40 text-xs mt-2 font-medium">
                            Optik form analiz ediliyor, lütfen bekleyin...
                        </p>
                    </div>
                )}

                {/* ─── STEP: PREVIEW ─── */}
                {step === 'preview' && (
                    <div className="h-full flex flex-col overflow-hidden">
                        {/* Stats bar */}
                        {stats && (
                            <div className="shrink-0 px-4 py-3 bg-slate-800/80 border-b border-white/5 flex items-center gap-4 text-xs font-bold">
                                <span className="text-green-400">✓ {stats.marked} İşaretli</span>
                                <span className="text-white/30">|</span>
                                <span className="text-yellow-400">○ {stats.empty} Boş</span>
                                <span className="text-white/30">|</span>
                                <span className="text-white/50">Toplam: {stats.total}</span>
                                <div className="flex-1" />
                                {debugImage && (
                                    <button
                                        onClick={() => setShowDebug(!showDebug)}
                                        className="text-blue-400 hover:text-blue-300 text-[10px] uppercase tracking-wider font-black"
                                    >
                                        {showDebug ? '📋 Sonuçlar' : '🔍 Debug'}
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Debug Image View */}
                        {showDebug && debugImage && (
                            <div className="flex-1 overflow-auto p-4 bg-black">
                                <img src={debugImage} className="w-full h-auto rounded-xl border border-blue-500/30" alt="Debug görüntüsü" />
                                {debugInfo && (
                                    <div className="mt-4 p-4 bg-slate-900 rounded-xl text-[10px] text-blue-200 font-mono space-y-1 border border-blue-500/20">
                                        <p className="text-blue-400 font-bold mb-2">TEKNİK BİLGİ</p>
                                        <p>Köşe Yöntemi: {debugInfo.cornerMethod || 'auto'}</p>
                                        <p>Görüntü: {debugInfo.imageSize?.w}x{debugInfo.imageSize?.h}</p>
                                        {debugInfo.sectionStats?.map((s: any, i: number) => (
                                            <div key={i} className="border-l-2 border-blue-500/30 pl-2 mt-2">
                                                <p className="font-bold text-white">{s.name}</p>
                                                <p>{s.count}/{s.total} işaretli (Yoğunluk: %{s.avgIntensity})</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Results Grid */}
                        {!showDebug && (
                            <div className="flex-1 overflow-y-auto px-4 py-4">
                                <p className="text-white/40 text-[10px] font-bold uppercase tracking-widest mb-4 text-center">
                                    Yanlış okunan cevapları düzeltmek için dokunun
                                </p>

                                {Object.entries(scanResults).map(([sectionName, answers]) => (
                                    <div key={sectionName} className="mb-6">
                                        {/* Section header */}
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="w-2 h-2 rounded-full bg-blue-500" />
                                            <h4 className="text-white font-black text-sm">{sectionName}</h4>
                                            <span className="text-white/30 text-[10px] font-bold ml-auto">
                                                {(answers as any[]).filter((a: any) => a.answer).length}/{(answers as any[]).length}
                                            </span>
                                        </div>

                                        {/* Questions grid */}
                                        <div className="grid grid-cols-5 sm:grid-cols-10 gap-1.5">
                                            {(answers as any[]).map((q: any, idx: number) => {
                                                const options = template.layout.sections.find(s => s.name === sectionName)?.grid.cols === 4
                                                    ? ['A', 'B', 'C', 'D']
                                                    : ['A', 'B', 'C', 'D', 'E'];

                                                return (
                                                    <div
                                                        key={idx}
                                                        className="bg-slate-800 rounded-xl p-2 border border-white/5 hover:border-blue-500/30 transition-all"
                                                    >
                                                        <p className="text-white/30 text-[9px] font-black text-center mb-1.5">
                                                            {q.questionNumber}
                                                        </p>
                                                        <div className="flex flex-col gap-0.5">
                                                            {options.map(opt => (
                                                                <button
                                                                    key={opt}
                                                                    onClick={() => handleEditAnswer(
                                                                        sectionName, idx,
                                                                        q.answer === opt ? '' : opt
                                                                    )}
                                                                    className={`w-full py-0.5 rounded text-[10px] font-black transition-all ${
                                                                        q.answer === opt
                                                                            ? 'bg-blue-500 text-white shadow-sm shadow-blue-500/30'
                                                                            : 'bg-white/5 text-white/25 hover:bg-white/10 hover:text-white/50'
                                                                    }`}
                                                                >
                                                                    {opt}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Bottom Actions */}
                        <div className="shrink-0 p-4 bg-gradient-to-t from-slate-900 to-transparent flex gap-3">
                            <button
                                onClick={handleRetry}
                                className="py-4 px-6 bg-white/10 hover:bg-white/15 text-white rounded-2xl font-bold text-sm transition-all border border-white/10"
                            >
                                ↻ Tekrar Tara
                            </button>
                            <button
                                onClick={handleConfirm}
                                className="flex-1 py-4 bg-green-600 hover:bg-green-700 text-white rounded-2xl font-black text-sm transition-all shadow-lg shadow-green-500/25 active:scale-95"
                            >
                                ✓ Sonuçları Onayla
                            </button>
                        </div>
                    </div>
                )}

                {/* Error Toast */}
                {error && (
                    <div className="absolute bottom-24 left-4 right-4 bg-red-500/90 text-white p-4 rounded-2xl shadow-xl backdrop-blur-md text-center font-bold text-sm animate-fade-in z-50">
                        <p>{error}</p>
                        <button
                            onClick={() => setError(null)}
                            className="mt-2 text-[10px] uppercase tracking-widest text-white/60 hover:text-white font-black"
                        >
                            Kapat
                        </button>
                    </div>
                )}
            </div>

            {/* Hidden elements */}
            <canvas ref={canvasRef} className="hidden" />
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileUpload}
                className="hidden"
            />
        </div>
    );
};
